import { useState, useEffect, useCallback, useRef } from 'react';
import { useCheckout } from '@/contexts/checkoutContext';
import { useApplePaySettings } from '@/contexts/applePayContext';
import { requiredForCountry } from '@/utils/addressConverter';
import { useApplePay } from '@/hooks/useApplePay';
import { isSafari } from '@/utils/isSafari';
//import { postData } from '@/utils/postData';
import { Endpoint, ValidationMessage } from '@/constants';
import { validatePhone } from '@/utils/validatePhone';
import { getAntiForgeryToken } from '@/utils/getAntiForgeryToken';
import { useApplePayDeliveryOptions } from '@/hooks/useApplePayDeliveryOptions';
import { handleCouponCode } from '@/hooks/couponHandler';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import {
  NotificationMessage,
  NotificationsQueue,
} from '@/components/Notifications';
import type {
  ApplePayOrder,
  ApplePayPayment,
  ExtendedCheckoutData,
  ApplePayOrderResponse,
} from '@/types/applePay';
import type { ApplePayPaymentAuthorizationResult } from '@/globals';
import type { CheckoutData } from '@/types';
import * as Styled from './ApplePay.styled';
import { buildScaData } from '@/utils/buildScaData';
import { Buffer } from 'buffer';

interface ApplePayButtonWrapperProps {
  style: string;
  buttonId: string;
  isBuyNow?: boolean; // Explicitly indicate if this is Buy Now mode
  productCode?: string; // For Buy Now mode, pass the product code
}

export const ApplePayButtonWrapper = ({
  style: _style,
  buttonId: _buttonId,
  isBuyNow = false,
  productCode,
}: ApplePayButtonWrapperProps) => {
  // Get Apple Pay settings from context
  const { settings: applePaySettings, loading: settingsLoading, error: settingsError, isAvailable } = useApplePaySettings();

  // Construct cartName from productCode for Buy Now mode
  const cartName =
    isBuyNow && productCode ? `BuyNow_${productCode}` : undefined;

  console.log('ApplePayButtonWrapper: Component mounted with props:', {
    style: _style,
    buttonId: _buttonId,
    applePaySettings: !!applePaySettings,
    settingsLoading,
    isBuyNow,
    productCode,
    cartName,
  });

  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const sessionValidationTokenRef = useRef<string>('');
  const currentShippingOptionsRef = useRef<any[]>([]);
  const selectedShippingOptionRef = useRef<string | null>(null);
  const processingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const currentCheckoutDataRef = useRef<CheckoutData | undefined>(undefined);
  const buyNowCartCreatedRef = useRef<boolean>(false);
  const currentAmountRef = useRef<number>(0);
  const currentShippingCountryRef = useRef<string>('IE');  // Default to IE

  // Only access checkout context for regular (non-Buy Now) mode
  const checkoutContext = !isBuyNow ? useCheckout() : null;

  // For Buy Now, we'll access cart data only after the button is clicked
  const checkoutData = checkoutContext?.checkoutData;
  const isRefreshing = checkoutContext?.isRefreshing;
  const refreshData = checkoutContext?.refreshCheckoutData;

  const { fetchDeliveryOptions } = useApplePayDeliveryOptions();

  // Use different hooks based on whether this is Buy Now or regular checkout
  const { createApplePayOrder, isPending: isRegularPending } = useApplePay({
    onError: () => {
      // Clear loading state on error
      console.log('ApplePay: Order creation failed');
      setIsProcessingPayment(false);
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
    },
    isBuyNow: isBuyNow,
  });

  const isPending = isRegularPending;

  // Helper function to get amount from cart data - only used for regular checkout
  const getAmount = (): number => {
    if (!isBuyNow && checkoutData?.amounts?.totalDecimal) {
      return checkoutData.amounts.totalDecimal;
    }

    // For Buy Now, we'll fetch the amount after cart creation
    return 0;
  };

  // Cast checkoutData to extended type for additional properties
  const extendedCheckoutData = checkoutData as CheckoutData &
    ExtendedCheckoutData;

  // Helper function to set shipping method in cart and return updated ShoppingCart
  const setShippingMethodInCart = async (
    shippingMethodName: string,
    cartNameParam?: string,
  ): Promise<any | null> => {
    try {
      console.log(
        'ApplePay: Setting shipping method in cart:',
        shippingMethodName,
      );

      const requestBody: { shippingMethodName: string; cartName?: string } = {
        shippingMethodName,
      };
      if (cartNameParam) {
        requestBody.cartName = cartNameParam;
      }

      const response = await fetch(Endpoint.SetShippingMethod, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          RequestVerificationToken: getAntiForgeryToken(),
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        console.warn(
          `ApplePay: Failed to set shipping method (HTTP ${response.status}):`,
          shippingMethodName,
        );
        return null;
      }

      const result = await response.json();
      if (result.isSuccess === false) {
        console.warn(
          'ApplePay: Shipping method update returned error:',
          result.messages,
        );
        return null;
      }

      console.log(
        'ApplePay: Shipping method set successfully:',
        shippingMethodName,
      );

      // Return the ShoppingCart data from the response
      if (result.data) {
        console.log(
          'ApplePay: Received updated ShoppingCart from SetShippingMethod:',
          result.data,
        );
        return result.data;
      } else {
        console.warn('ApplePay: SetShippingMethod response missing data field');
        return null;
      }
    } catch (error) {
      console.error('ApplePay: Error setting shipping method:', error);
      return null;
    }
  };

  // Helper function to update CheckoutData with ShoppingCart amounts
  const updateCheckoutDataWithShoppingCart = (
    currentCheckoutData: CheckoutData | undefined,
    shoppingCart: any,
  ): CheckoutData => {
    if (!currentCheckoutData) {
      // Create minimal CheckoutData if none exists
      return {
        amounts: {
          discount: shoppingCart.amounts?.discount || '',
          giftCard: shoppingCart.amounts?.giftCard || [],
          loyaltyCard: shoppingCart.amounts?.loyaltyCard || '',
          shipping: shoppingCart.amounts?.shipping || '0.00',
          subtotal: shoppingCart.amounts?.subtotal || '0.00',
           linesubtotal: shoppingCart.amounts?.linesubtotal || '0.00',
          tax: shoppingCart.amounts?.tax || '0.00',
          total: shoppingCart.amounts?.total || '0.00',
          totalDecimal: shoppingCart.amounts?.totalDecimal || 0,
          discountDecimal: shoppingCart.amounts?.discountDecimal || 0,
          subtotalDecimal: shoppingCart.amounts?.subtotalDecimal || 0,
          taxDecimal: shoppingCart.amounts?.taxDecimal || 0,
          shippingDecimal: shoppingCart.amounts?.shippingDecimal || 0,
          loyaltyCardDecimal: shoppingCart.amounts?.loyaltyCardDecimal || 0,
          giftCardTotalDecimal: shoppingCart.amounts?.giftCardTotalDecimal || 0,

        },
        cartId: shoppingCart.cartId || '',
        cartName: shoppingCart.cartName ?? '',
        clickAndCollectLocations: [],
        discountCode: shoppingCart.discountCode || '',
        emailAddress: shoppingCart.emailAddress,
        hasBalance: shoppingCart.hasBalance || false,
        isClickAndCollectAvailable:
        shoppingCart.isClickAndCollectAvailable || false,
        items: shoppingCart.items || [],
        loyaltyCardId: shoppingCart.loyaltyCardId ?? '',
        promotions: shoppingCart.promotions || [],
        shippingMethod: shoppingCart.shippingMethod,
      };
    }

    // Update existing CheckoutData with new amounts
    return {
      ...currentCheckoutData,
      amounts: {
        discount:
          shoppingCart.amounts?.discount ||
          currentCheckoutData.amounts?.discount,
        giftCard:
          shoppingCart.amounts?.giftCard ||
          currentCheckoutData.amounts?.giftCard,
        loyaltyCard:
          shoppingCart.amounts?.loyaltyCard ||
          currentCheckoutData.amounts?.loyaltyCard,
        shipping:
          shoppingCart.amounts?.shipping ||
          currentCheckoutData.amounts?.shipping,
        subtotal:
          shoppingCart.amounts?.subtotal ||
          currentCheckoutData.amounts?.subtotal,
        linesubtotal:
          shoppingCart.amounts?.linesubtotal ||
          currentCheckoutData.amounts?.linesubtotal,
        tax: shoppingCart.amounts?.tax || currentCheckoutData.amounts?.tax,
        total:
          shoppingCart.amounts?.total || currentCheckoutData.amounts?.total,
        totalDecimal:
          shoppingCart.amounts?.totalDecimal ??
          currentCheckoutData.amounts?.totalDecimal,
        discountDecimal:
          shoppingCart.amounts?.discountDecimal ??
          currentCheckoutData.amounts?.discountDecimal,
        subtotalDecimal:
          shoppingCart.amounts?.subtotalDecimal ??
          currentCheckoutData.amounts?.subtotalDecimal,
        taxDecimal:
          shoppingCart.amounts?.taxDecimal ??
          currentCheckoutData.amounts?.taxDecimal,
        shippingDecimal:
          shoppingCart.amounts?.shippingDecimal ??
          currentCheckoutData.amounts?.shippingDecimal,
        loyaltyCardDecimal:
          shoppingCart.amounts?.loyaltyCardDecimal ??
          currentCheckoutData.amounts?.loyaltyCardDecimal,
        giftCardTotalDecimal:
          shoppingCart.amounts?.giftCardTotalDecimal ??
          currentCheckoutData.amounts?.giftCardTotalDecimal,
        couponDiscountDecimal:
          shoppingCart.amounts?.couponDiscountDecimal ??
          currentCheckoutData.amounts?.couponDiscountDecimal,
      },
      // Update other cart properties that might have changed
      cartId: shoppingCart.cartId || currentCheckoutData.cartId,
      discountCode: shoppingCart.discountCode,
      hasBalance: shoppingCart.hasBalance ?? currentCheckoutData.hasBalance,
      items: shoppingCart.items || currentCheckoutData.items,
      promotions: shoppingCart.promotions || currentCheckoutData.promotions,
    };
  };

  // Helper function to build Apple Pay line items
  const buildApplePayLineItems = (dataSource?: CheckoutData) => {
    const lineItems: Array<{ label: string; amount: string; type?: 'final' | 'pending' }> = [];

    if (dataSource?.amounts) {
      lineItems.push({
        label: 'Subtotal',
        amount: dataSource.amounts.subtotalDecimal.toFixed(2),
      });

      // Add regular discount first (if present)
      if (dataSource.amounts.discountDecimal) {
        const roundedDiscount = parseFloat(dataSource.amounts.discountDecimal.toFixed(2));
        if (roundedDiscount > 0) {
          console.log('ApplePay: Building regular discount line item:', {
            amount: dataSource.amounts.discountDecimal
          });

          lineItems.push({
            label: 'Discount',
            amount: `-${roundedDiscount.toFixed(2)}`,
          });
        }
      }

      // Add coupon discount second (if present)
      if (dataSource.amounts.couponDiscountDecimal) {
        const roundedCouponDiscount = parseFloat(dataSource.amounts.couponDiscountDecimal.toFixed(2));
        if (roundedCouponDiscount > 0) {
          const couponCode = dataSource.discountCode || 'COUPON';

          console.log('ApplePay: Building coupon discount line item:', {
            couponCode: couponCode,
            amount: dataSource.amounts.couponDiscountDecimal
          });

          lineItems.push({
            label: couponCode,
            amount: `-${roundedCouponDiscount.toFixed(2)}`,
          });
        }
      }

      // Add loyalty card if present
      if (
        dataSource.amounts.loyaltyCardDecimal &&
        dataSource.amounts.loyaltyCardDecimal > 0
      ) {
        lineItems.push({
          label: 'Loyalty Card',
          amount: `-${dataSource.amounts.loyaltyCardDecimal.toFixed(2)}`,
        });
      }

      // Add gift cards if present
      if (
        dataSource.amounts.giftCardTotalDecimal &&
        dataSource.amounts.giftCardTotalDecimal > 0
      ) {
        lineItems.push({
          label: 'Gift Card',
          amount: `-${dataSource.amounts.giftCardTotalDecimal.toFixed(2)}`,
        });
      }

      // Add shipping if present (from backend amounts)
      if (
        dataSource.amounts.shippingDecimal &&
        dataSource.amounts.shippingDecimal > 0
      ) {
        lineItems.push({
          label: 'Shipping',
          amount: dataSource.amounts.shippingDecimal.toFixed(2),
        });
      }

      // Add tax if present
      if (dataSource.amounts.taxDecimal && dataSource.amounts.taxDecimal > 0) {
        lineItems.push({
          label: 'Tax',
          amount: dataSource.amounts.taxDecimal.toFixed(2),
        });
      }
    } else {
      // Fallback — Buy Now before cart is created; mark as pending so the
      // Apple Pay sheet doesn't flash "€0.00".
      lineItems.push({
        label: 'Subtotal',
        amount: '0.00',
        type: 'pending',
      });
    }

    return lineItems;
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
    };
  }, []);

  // Handle loading state
  if (settingsLoading) {
    console.log('ApplePay: Loading settings...');
    return (
      <Styled.UnavailableMessage>
        Loading Apple Pay...
      </Styled.UnavailableMessage>
    );
  }

  // Handle error state
  if (settingsError || !applePaySettings) {
    console.error('ApplePay: Settings error:', settingsError);
    return (
      <Styled.UnavailableMessage>
        Apple Pay: Configuration error
      </Styled.UnavailableMessage>
    );
  }

  // Debug log the settings
  console.log('ApplePay: Settings loaded from context:', applePaySettings);

  // Primary check: Is Apple Pay enabled AND available?
  // The context's isAvailable already validated:
  // - isSafari() returned true
  // - ApplePaySession exists
  // - canMakePayments() returned true
  if (!applePaySettings.enabled) {
    console.log('ApplePay: Disabled via backend settings', {
      backendEnabled: applePaySettings.enabled,
      browserAvailable: isAvailable
    });
    // Don't render anything if disabled
    return null;
  }

  if (!isAvailable) {
    console.log('ApplePay: Not available in this browser', {
      isSafari: isSafari(),
      hasApplePaySession: !!window.ApplePaySession,
      contextAvailable: isAvailable
    });
    // Don't render anything if unavailable
    return null;
  }

  // If we reach here, both backend allows it AND browser supports it
  console.log('ApplePay: Rendering button', {
    enabled: applePaySettings.enabled,
    available: isAvailable
  });

  // ################################
  // ##### Button click handler #####
  // ################################
  const handleApplePayClick = useCallback(async () => {
    // Trust the context's isAvailable check - it's already validated everything
    // This is just a defensive runtime check in case context state is stale
    if (!isAvailable) {
      console.error('ApplePay: Not available (context check failed)');
      NotificationsQueue.add(
        {
          notification: (
            <NotificationMessage $isError={true}>
              Apple Pay is not available
            </NotificationMessage>
          ),
        },
        { timeout: 5000 },
      );
      return;
    }

    // Additional defensive check - if somehow context is wrong and window objects don't exist
    if (!window.ApplePaySession || typeof window.ApplePaySession.canMakePayments !== 'function') {
      console.error('ApplePay: Runtime validation failed - ApplePaySession not properly available');
      NotificationsQueue.add(
        {
          notification: (
            <NotificationMessage $isError={true}>
              Apple Pay is not available in this browser
            </NotificationMessage>
          ),
        },
        { timeout: 5000 },
      );
      return;
    }

    // Re-check canMakePayments at runtime (user might have changed settings)
    if (!window.ApplePaySession.canMakePayments()) {
      console.error('ApplePay: canMakePayments returned false at runtime');
      NotificationsQueue.add(
        {
          notification: (
            <NotificationMessage $isError={true}>
              Apple Pay is not set up on this device
            </NotificationMessage>
          ),
        },
        { timeout: 5000 },
      );
      return;
    }

    console.log('ApplePay: Button clicked, starting payment session');

    let currentAmount = 0;

    if (isBuyNow) {
      // For Buy Now, start with 0 amount - real amount will be set after cart creation
      if (!productCode) {
        console.error('ApplePay: Product code required for Buy Now');
        return;
      }

      currentAmount = 0;
      console.log(
        'ApplePay: Using initial amount 0 for Buy Now - will update after cart creation',
      );

      // Reset cart creation flag for new session
      buyNowCartCreatedRef.current = false;
    } else {
      // Regular checkout - use existing cart data
      currentAmount = getAmount();
      currentCheckoutDataRef.current = checkoutData;
    }

    // Store current amount in ref for use across handlers
    currentAmountRef.current = currentAmount;

    console.log('ApplePay Debug: Final payment request amount:', currentAmount);

    if (currentAmount < 0 || (!isBuyNow && currentAmount <= 0)) {
      console.error('ApplePay: Invalid amount', currentAmount);
      NotificationsQueue.add(
        {
          notification: (
            <NotificationMessage $isError={true}>
              Unable to process payment. Please refresh and try again.
            </NotificationMessage>
          ),
        },
        { timeout: 5000 },
      );
      return;
    }

    // Coupon support is configurable via backend settings
    const supportsCouponCode = applePaySettings.isCouponEntryEnabled ?? false;
    console.log('ApplePay: applePaySettings:', applePaySettings);

    const paymentRequest = {
      countryCode: applePaySettings.countryCode || 'IE',
      currencyCode: applePaySettings.currencyCode || 'EUR',
      supportedNetworks: applePaySettings.supportedNetworks || [
        'visa',
        'masterCard',
        'amex',
        'discover',
      ],
      merchantCapabilities: applePaySettings.merchantCapabilities || [
        'supports3DS',
      ],
      total: {
        label: applePaySettings.merchantName || 'Eason',
        amount: currentAmount.toFixed(2),
        ...(isBuyNow && { type: 'pending' as const }),
      },
      lineItems: buildApplePayLineItems(isBuyNow ? undefined : checkoutData),
      requiredShippingContactFields: ['email','name', 'postalAddress', 'phone'],
      requiredBillingContactFields: ['email','name', 'postalAddress', 'phone'],
      shippingType: 'shipping',
      // Add coupon support if feature is available
      ...(supportsCouponCode && {
        supportsCouponCode: true,
        couponCode: checkoutData?.discountCode || ''
      })
    };

    console.log(
      'ApplePay Debug: Creating session with initial amount:',
      currentAmount.toFixed(2),
    );
    console.log('ApplePay: Creating session with request:', paymentRequest);
    const session = new window.ApplePaySession(12, paymentRequest);

    // #####################################
    // #### Merchant validation handler ####
    // #####################################
    session.onvalidatemerchant = async event => {
      console.log('ApplePay: Validating merchant', event.validationURL);
      try {
        const response = await fetch('/Checkout/ValidateMerchantForApplePay', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ validationURL: event.validationURL }),
        });

        if (!response.ok) {
          throw new Error(`Merchant validation failed: ${response.status}`);
        }

        const data = await response.json();
        console.log('ApplePay: Merchant validation successful');
        session.completeMerchantValidation(data.applePaySession);
        sessionValidationTokenRef.current = data.sessionValidationToken || '';
      } catch (error) {
        console.error('ApplePay: Merchant validation error:', error);
        session.abort();
        NotificationsQueue.add(
          {
            notification: (
              <NotificationMessage $isError={true}>
                Apple Pay setup failed. Please try again.
              </NotificationMessage>
            ),
          },
          { timeout: 5000 },
        );
      }
    };

    // ############################################
    // #### Shipping contact selection handler ####
    // ############################################
    session.onshippingcontactselected = async event => {
      console.log('ApplePay: Shipping contact selected', event.shippingContact);
      console.log(
        'ApplePay: Country code for shipping options:',
        event.shippingContact?.countryCode,
      );

      // Store the shipping country for use in other handlers (e.g., coupon code)
      currentShippingCountryRef.current = event.shippingContact?.countryCode || 'IE';

      try {
        // Validate address for delivery
        console.log('ApplePay: Validating shipping address for delivery');
        if(!applePaySettings.validCountries.includes(event.shippingContact?.countryCode || '')){
          console.warn('ApplePay: Delivery not supported to this country:', event.shippingContact?.countryCode);
          session.completeShippingContactSelection({
            newTotal: {
              label: applePaySettings.merchantName || 'Eason',
              amount: 0.00.toFixed(2),
            },
            errors: [new window.ApplePayError('shippingContactInvalid', 'countryCode', 'Delivery to this country is not supported')],
          });
          return;
        }

        // For Buy Now, create cart if not already created
        if (isBuyNow && !buyNowCartCreatedRef.current) {
          console.log(
            'ApplePay: Creating Buy Now cart in shipping contact selection',
          );

          const addResponse = await fetch(Endpoint.AddToBuyNowCart, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              RequestVerificationToken: getAntiForgeryToken(),
            },
            body: JSON.stringify({
              code: productCode,
              quantity: 1,
              cartName: cartName,
            }),
          });

          if (!addResponse.ok) {
            throw new Error('Failed to create Buy Now cart');
          }

          // Fetch the created cart data
          const cartResponse = await fetch(
            `${Endpoint.GetCart}?cartName=${encodeURIComponent(cartName ?? '')}`,
            {
              method: 'GET',
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                RequestVerificationToken: getAntiForgeryToken(),
              },
            },
          );

          if (!cartResponse.ok) {
            throw new Error('Failed to fetch Buy Now cart data');
          }

          const cartResponseData = await cartResponse.json();
          const buyNowCheckoutData = cartResponseData.data;

          // Store the checkout data in ref for use in other handlers
          currentCheckoutDataRef.current = buyNowCheckoutData;
          buyNowCartCreatedRef.current = true;

          // Update the current amount with actual cart total (includes everything)
          currentAmountRef.current =
            buyNowCheckoutData?.amounts?.totalDecimal || 0;

          console.log(
            'ApplePay: Buy Now cart created with amount:',
            currentAmountRef.current,
          );
        }

        // Set delivery country in cart for both Buy Now and regular checkout
        if (event.shippingContact) {
          console.log('ApplePay: Setting delivery country in cart');
          const countryCode = event.shippingContact.countryCode || 'IE';
          console.log('ApplePay: Country code:', countryCode);

          try {
            const requestBody: { countryCode: string; cartName?: string } = {
              countryCode,
            };
            if (isBuyNow && cartName) {
              requestBody.cartName = cartName;
            }

            const setCountryResponse = await fetch(
              Endpoint.SetDeliveryCountry,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  RequestVerificationToken: getAntiForgeryToken(),
                },
                body: JSON.stringify(requestBody),
              },
            );

            if (!setCountryResponse.ok) {
              console.warn(
                `ApplePay: Failed to set delivery country (HTTP ${setCountryResponse.status}), continuing with shipping options`,
              );
            } else {
              const setCountryResult = await setCountryResponse.json();
              if (setCountryResult.isSuccess === false) {
                console.warn(
                  'ApplePay: Delivery country update returned error:',
                  setCountryResult.messages,
                );
              } else {
                console.log('ApplePay: Delivery country set successfully');

                // Use the ShoppingCart data returned from SetDeliveryCountry
                if (setCountryResult.data) {
                  console.log(
                    'ApplePay: Updating cart data with response from SetDeliveryCountry',
                  );
                  const updatedShoppingCart = setCountryResult.data;

                  // Update CheckoutData with new amounts from ShoppingCart
                  const updatedCheckoutData =
                    updateCheckoutDataWithShoppingCart(
                      currentCheckoutDataRef.current,
                      updatedShoppingCart,
                    );
                  currentCheckoutDataRef.current = updatedCheckoutData;

                  // Update current amount ref with backend total
                  const previousAmount = currentAmountRef.current;
                  const newAmount =
                    updatedCheckoutData.amounts?.totalDecimal || 0;
                  currentAmountRef.current = newAmount;

                  console.log(
                    'ApplePay: Updated cart amount after country change:',
                    {
                      previousAmount,
                      newAmount,
                      difference: newAmount - previousAmount,
                    },
                  );
                } else {
                  console.warn(
                    'ApplePay: SetDeliveryCountry response missing data field',
                  );
                }
              }
            }
          } catch (countryError) {
            console.error(
              'ApplePay: Error setting delivery country:',
              countryError,
            );
            // Continue with shipping options even if country setting fails
            console.log(
              'ApplePay: Continuing with shipping options despite country error',
            );
          }
        } else {
          console.warn(
            'ApplePay: No shipping contact provided, skipping country update',
          );
        }

        // For regular checkout, refresh cart data after country is set
        if (!isBuyNow && refreshData) {
          console.log(
            'ApplePay: Refreshing checkout data after country update',
          );
          await refreshData();
        }

        // Fetch shipping options for the selected country
        const shippingOptionsResponse = await fetchDeliveryOptions(
          event.shippingContact?.countryCode || 'IE',
          isBuyNow,
          productCode,
        );

        if (!shippingOptionsResponse.isDeliverySupported) {
          session.completeShippingContactSelection({
            errors: [ new window.ApplePayError('shippingContactInvalid', 'countryCode', shippingOptionsResponse.unsupportedMessage) ],
          });
          return;
        }

        // Convert to Apple Pay format
        const applePayShippingMethods = shippingOptionsResponse.options.map(
          option => ({
            identifier: option.identifier,
            label: option.label,
            detail: option.detail,
            amount: option.amount,
          }),
        );
        console.log(
          'ApplePay: Shipping options received:',
          applePayShippingMethods,
        );

        // Store current shipping options
        currentShippingOptionsRef.current = shippingOptionsResponse.options;

        // Find default option
        const defaultOption =
          shippingOptionsResponse.options.find(opt => opt.isDefault) ||
          shippingOptionsResponse.options[0];
        if (defaultOption) {
          selectedShippingOptionRef.current = defaultOption.identifier; // update ref immediately
          console.log(
            'ApplePay: Default shipping option selected:',
            defaultOption,
          );

          // Set default shipping method in cart and update amounts
          setShippingMethodInCart(
            defaultOption.identifier,
            isBuyNow ? cartName : undefined,
          )
            .then(updatedShoppingCart => {
              if (updatedShoppingCart) {
                console.log(
                  'ApplePay: Default shipping method synchronized with cart:',
                  defaultOption.identifier,
                );

                // Update CheckoutData with new amounts from ShoppingCart
                const updatedCheckoutData = updateCheckoutDataWithShoppingCart(
                  currentCheckoutDataRef.current,
                  updatedShoppingCart,
                );
                currentCheckoutDataRef.current = updatedCheckoutData;

                // Update current amount ref with backend total (includes shipping, tax, everything)
                const backendTotal =
                  updatedCheckoutData.amounts?.totalDecimal ?? 0;
                const previousAmount = currentAmountRef.current;
                currentAmountRef.current = backendTotal;

                console.log(
                  'ApplePay: Updated amounts after default shipping method change:',
                  {
                    previousAmount,
                    newTotal: backendTotal,
                    difference: backendTotal - previousAmount,
                    updatedAmounts: updatedCheckoutData.amounts,
                  },
                );

                // Update Apple Pay with new shipping methods and backend total
                const newLineItems =
                  buildApplePayLineItems(updatedCheckoutData);
                console.log(
                  'ApplePay Debug: Updating Apple Pay with backend total:',
                  backendTotal.toFixed(2),
                );
                console.log(
                  'ApplePay: Updating Apple Pay display with new line items after address change:',
                  newLineItems,
                );

                session.completeShippingContactSelection({
                  newTotal: {
                    label: applePaySettings.merchantName || 'Eason',
                    amount: backendTotal.toFixed(2),
                  },
                  newLineItems: newLineItems,
                  newShippingMethods: applePayShippingMethods,
                });
              } else {
                console.warn(
                  'ApplePay: Failed to synchronize default shipping method with cart:',
                  defaultOption.identifier,
                );
              }
            })
            .catch(error => {
              console.warn(
                'ApplePay: Error setting default shipping method in cart:',
                error,
              );
            });
        }
      } catch (error) {
        console.error(
          'ApplePay: Error handling shipping contact selection:',
          error,
        );
        session.completeShippingContactSelection({
          newTotal: {
            label: applePaySettings.merchantName || 'Eason',
            amount: 0.00.toFixed(2),
          },
          errors: [new window.ApplePayError('shippingContactInvalid', 'countryCode', 'Delivery to this country is not supported')],
        });
      }
    };

    // ###########################################
    // #### Shipping method selection handler ####
    // ###########################################
    session.onshippingmethodselected = async event => {
      console.log('ApplePay: Shipping method selected', event.shippingMethod);

      try {
        // Update selected shipping method
        selectedShippingOptionRef.current = event.shippingMethod.identifier; // update ref immediately
        console.log(
          'ApplePay: Selected shipping method:',
          event.shippingMethod,
        );

        // Set selected shipping method in cart and update amounts
        setShippingMethodInCart(
          event.shippingMethod.identifier,
          isBuyNow ? cartName : undefined,
        )
          .then(updatedShoppingCart => {
            if (updatedShoppingCart) {
              console.log(
                'ApplePay: User-selected shipping method synchronized with cart:',
                event.shippingMethod.identifier,
              );

              // Update CheckoutData with new amounts from ShoppingCart
              const updatedCheckoutData = updateCheckoutDataWithShoppingCart(
                currentCheckoutDataRef.current,
                updatedShoppingCart,
              );
              currentCheckoutDataRef.current = updatedCheckoutData;

              // Update current amount ref with backend total (includes shipping, tax, everything)
              const backendTotal =
                updatedCheckoutData.amounts?.totalDecimal ?? 0;
              const previousAmount = currentAmountRef.current;
              currentAmountRef.current = backendTotal;

              console.log(
                'ApplePay: Updated amounts after user shipping method change:',
                {
                  previousAmount,
                  newTotal: backendTotal,
                  difference: backendTotal - previousAmount,
                  shippingMethod: event.shippingMethod.identifier,
                  updatedAmounts: updatedCheckoutData.amounts,
                },
              );

              // Update Apple Pay with new total and line items from backend
              const newLineItems = buildApplePayLineItems(updatedCheckoutData);
              console.log(
                'ApplePay Debug: Updating Apple Pay with backend total:',
                backendTotal.toFixed(2),
              );
              console.log(
                'ApplePay: Updating Apple Pay display with new line items:',
                newLineItems,
              );

              session.completeShippingMethodSelection({
                newTotal: {
                  label: applePaySettings.merchantName || 'Eason',
                  amount: backendTotal.toFixed(2),
                },
                newLineItems: newLineItems,
              });
            } else {
              console.warn(
                'ApplePay: Failed to synchronize user-selected shipping method with cart:',
                event.shippingMethod.identifier,
              );
            }
          })
          .catch(error => {
            console.warn(
              'ApplePay: Error setting user-selected shipping method in cart:',
              error,
            );
          });
      } catch (error) {
        console.error(
          'ApplePay: Error handling shipping method selection:',
          error,
        );
      }
    };

    // ############################################
    // #### Coupon code change handler ####
    // ############################################
    if (supportsCouponCode) {
      session.oncouponcodechanged = async event => {
        console.log('ApplePay: Coupon code changed', event.couponCode);

        // Apply or clear coupon code
        const couponUpdate = await handleCouponCode(
          event.couponCode,
          isBuyNow ? cartName : undefined,
          currentShippingCountryRef.current
        );

        // Check if the coupon operation was successful
        if (!couponUpdate.success) {
          console.warn('ApplePay: Coupon operation failed', couponUpdate.errors);

          // Convert errors to ApplePayError objects
          const applePayErrors = couponUpdate.errors?.map(error => {
            // For connection errors, use a generic message
            if (error.code === 'connectionError') {
              return new window.ApplePayError('couponCodeInvalid', undefined, error.message);
            }
            // For invalid coupon errors, pass the backend message
            return new window.ApplePayError('couponCodeInvalid', undefined, error.message);
          }) || [new window.ApplePayError('couponCodeInvalid')];

          // Report errors to Apple Pay - must include all fields to maintain display
          const currentLineItems = currentCheckoutDataRef.current
            ? buildApplePayLineItems(currentCheckoutDataRef.current)
            : [];

          // Ensure shipping methods array is valid and properly formatted
          const shippingMethods = currentShippingOptionsRef.current && currentShippingOptionsRef.current.length > 0
            ? currentShippingOptionsRef.current.map(option => ({
                identifier: option.identifier || option.type || '',
                label: option.label || option.title || '',
                detail: option.detail || option.description || '',
                amount: option.amount || '0.00',
              }))
            : [];

          // Debug logging to identify the issue
          console.log('ApplePay: Coupon error response data:', {
            hasCheckoutData: !!currentCheckoutDataRef.current,
            lineItemsCount: currentLineItems.length,
            lineItems: currentLineItems,
            shippingOptionsCount: shippingMethods.length,
            shippingMethods: shippingMethods,
            currentAmount: currentAmountRef.current,
            errors: applePayErrors
          });

          session.completeCouponCodeChange({
            newTotal: {
              label: applePaySettings.merchantName || 'Eason',
              amount: currentAmountRef.current.toFixed(2),  // Keep the current total unchanged
            },
            newLineItems: currentLineItems.length > 0 ? currentLineItems : undefined,  // Only include if we have items
            newShippingMethods: shippingMethods.length > 0 ? shippingMethods : undefined,  // Only include if we have methods
            errors: applePayErrors
          });
          return;
        }

        // Update checkout data ref with new cart data
        if (couponUpdate.cartData) {
          const updatedCheckoutData = updateCheckoutDataWithShoppingCart(
            currentCheckoutDataRef.current,
            couponUpdate.cartData
          );
          currentCheckoutDataRef.current = updatedCheckoutData;

          // Debug: Check if discountCode is in the updated data
          console.log('ApplePay: After update, discountCode in checkout data:', updatedCheckoutData.discountCode);

          // Update amount with new total from backend
          const previousAmount = currentAmountRef.current;
          currentAmountRef.current = updatedCheckoutData.amounts?.totalDecimal ?? 0;

          console.log('ApplePay: Updated amounts after coupon code change:', {
            previousAmount,
            newTotal: currentAmountRef.current,
            discountCode: event.couponCode,
            checkoutDataDiscountCode: updatedCheckoutData.discountCode,
            discountAmount: updatedCheckoutData.amounts?.discountDecimal
          });
        }

        // Update shipping options if provided
        if (couponUpdate.quickPayShippingMethods && couponUpdate.quickPayShippingMethods.length > 0) {
          currentShippingOptionsRef.current = couponUpdate.quickPayShippingMethods.map(option => ({
            identifier: option.identifier,
            label: option.label,
            detail: option.detail || '',
            amount: option.amount,
          }));
        }

        // Build Apple Pay update
        console.log('ApplePay: Before building line items, checkout data has:', {
          discountCode: currentCheckoutDataRef.current?.discountCode,
          discountAmount: currentCheckoutDataRef.current?.amounts?.discountDecimal
        });
        const newLineItems = buildApplePayLineItems(currentCheckoutDataRef.current);

        session.completeCouponCodeChange({
          newTotal: {
            label: applePaySettings.merchantName || 'Eason',
            amount: currentAmountRef.current.toFixed(2),
          },
          newLineItems: newLineItems,
          newShippingMethods: currentShippingOptionsRef.current.map(option => ({
            identifier: option.identifier,
            label: option.label,
            detail: option.detail,
            amount: option.amount,
          }))
        });
      };
    }

    // #######################################
    // #### Payment authorization handler ####
    // #######################################
    session.onpaymentauthorized = async event => {
      console.log('ApplePay: Payment authorized, processing order...');

      // Show loading state immediately when Apple Pay returns
      setIsProcessingPayment(true);

      // Set a timeout to clear stuck loading states (30 seconds)
      processingTimeoutRef.current = setTimeout(() => {
        console.warn(
          'ApplePay: Processing timeout reached, clearing loading state',
        );
        setIsProcessingPayment(false);
      }, 30000);

      try {
        // Extract payment data
        console.log('ApplePay: Payment data received:', event.payment);
        const payment = event.payment as ApplePayPayment;

        const errors = [];

        // Billing Contact - FirstName
        if(!payment.billingContact?.givenName) {
          errors.push(new window.ApplePayError('billingContactInvalid', 'givenName', 'First name is required'));
        } else if (payment.billingContact.givenName.length > 20) {
          errors.push(new window.ApplePayError('billingContactInvalid', 'givenName', ValidationMessage.FirstNameTooLong));
        }

        // Billing Contact - LastName
        if(!payment.billingContact?.familyName) {
          errors.push(new window.ApplePayError('billingContactInvalid', 'familyName', 'Last name is required'));
        } else if (payment.billingContact.familyName.length > 20) {
          errors.push(new window.ApplePayError('billingContactInvalid', 'familyName', ValidationMessage.LastNameTooLong));
        }

        // Billing Contact - AddressLine1
        if(!payment.billingContact?.addressLines?.[0]) {
          errors.push(new window.ApplePayError('billingContactInvalid', 'addressLines', 'Address line 1 is required'));
        } else if (payment.billingContact.addressLines[0].length > 50) {
          errors.push(new window.ApplePayError('billingContactInvalid', 'addressLines', ValidationMessage.AddressLineTooLong));
        }

        // Billing Contact - City
        if(!payment.billingContact?.locality) {
          errors.push(new window.ApplePayError('billingContactInvalid', 'locality', 'City is required'));
        } else if (payment.billingContact.locality.length > 50) {
          errors.push(new window.ApplePayError('billingContactInvalid', 'locality', ValidationMessage.CityTooLong));
        }

        // Billing Contact - County (optional, based on country)
        if(!payment.billingContact?.administrativeArea && requiredForCountry(payment.billingContact?.countryCode || '')) {
          errors.push(new window.ApplePayError('billingContactInvalid', 'administrativeArea', 'County is required'));
        }

        // Billing Contact - PostalCode (optional, based on country)
        if(!payment.billingContact?.postalCode && requiredForCountry(payment.billingContact?.countryCode || '')) {
          errors.push(new window.ApplePayError('billingContactInvalid', 'postalCode', 'Postal code is required'));
        }

        // Billing Contact - CountryCode
        if(!payment.billingContact?.countryCode) {
          errors.push(new window.ApplePayError('billingContactInvalid', 'countryCode', 'Country is required'));
        }

        // Shipping Contact - PhoneNumber
        if(!payment.shippingContact?.phoneNumber) {
          errors.push(new window.ApplePayError('shippingContactInvalid', 'phoneNumber', 'Phone number is required'));
        } else {
          // Validate phone format (8-15 digits)
          if (!validatePhone(payment.shippingContact.phoneNumber)) {
            errors.push(new window.ApplePayError('shippingContactInvalid', 'phoneNumber', ValidationMessage.InvalidPhone));
          }
          // Validate phone total length (<= 19 chars including formatting)
          if (payment.shippingContact.phoneNumber.length > 19) {
            errors.push(new window.ApplePayError('shippingContactInvalid', 'phoneNumber', ValidationMessage.PhoneTooLong));
          }
        }

        // Shipping Contact - Email
        if(!payment.shippingContact?.emailAddress) {
          errors.push(new window.ApplePayError('shippingContactInvalid', 'emailAddress', 'Email address is required'));
        } else if (payment.shippingContact.emailAddress.length > 80) {
          errors.push(new window.ApplePayError('shippingContactInvalid', 'emailAddress', ValidationMessage.EmailTooLong));
        }

        // Shipping Contact - FirstName
        if(!payment.shippingContact?.givenName) {
          errors.push(new window.ApplePayError('shippingContactInvalid', 'givenName', 'First name is required'));
        } else if (payment.shippingContact.givenName.length > 20) {
          errors.push(new window.ApplePayError('shippingContactInvalid', 'givenName', ValidationMessage.FirstNameTooLong));
        }

        // Shipping Contact - LastName
        if(!payment.shippingContact?.familyName) {
          errors.push(new window.ApplePayError('shippingContactInvalid', 'familyName', 'Last name is required'));
        } else if (payment.shippingContact.familyName.length > 20) {
          errors.push(new window.ApplePayError('shippingContactInvalid', 'familyName', ValidationMessage.LastNameTooLong));
        }

        // Shipping Contact - AddressLine1
        if(!payment.shippingContact?.addressLines?.[0]) {
          errors.push(new window.ApplePayError('shippingContactInvalid', 'addressLines', 'Address line 1 is required'));
        } else if (payment.shippingContact.addressLines[0].length > 50) {
          errors.push(new window.ApplePayError('shippingContactInvalid', 'addressLines', ValidationMessage.AddressLineTooLong));
        }

        // Shipping Contact - City
        if(!payment.shippingContact?.locality) {
          errors.push(new window.ApplePayError('shippingContactInvalid', 'locality', 'City is required'));
        } else if (payment.shippingContact.locality.length > 50) {
          errors.push(new window.ApplePayError('shippingContactInvalid', 'locality', ValidationMessage.CityTooLong));
        }

        // Shipping Contact - County (optional, based on country)
        if(!payment.shippingContact?.administrativeArea && requiredForCountry(payment.shippingContact?.countryCode || '')) {
          errors.push(new window.ApplePayError('shippingContactInvalid', 'administrativeArea', 'County is required'));
        }

        // Shipping Contact - PostalCode (optional, based on country)
        if(!payment.shippingContact?.postalCode && requiredForCountry(payment.shippingContact?.countryCode || '')) {
          errors.push(new window.ApplePayError('shippingContactInvalid', 'postalCode', 'Postal code is required'));
        }

        // Shipping Contact - CountryCode
        if(!payment.shippingContact?.countryCode) {
          errors.push(new window.ApplePayError('shippingContactInvalid', 'countryCode', 'Country is required'));
        } else if(!applePaySettings.validCountries.includes(payment.shippingContact.countryCode)) {
          errors.push(new window.ApplePayError('shippingContactInvalid', 'countryCode', 'Delivery not available to selected country'));
        }

        errors.forEach(error => {
          console.log({
            code: error.code,
            contactField: error.contactField,
            message: error.message
          });
        });

        if(errors.length > 0) {
          
          const result: ApplePayPaymentAuthorizationResult = {
            status: window.ApplePaySession.STATUS_FAILURE,
            errors: errors
          };

          session.completePayment(result);

          // Clear loading state
          setIsProcessingPayment(false);
          if (processingTimeoutRef.current) {
            clearTimeout(processingTimeoutRef.current);
          }
          return;
        }

        const convertApplePayAddress = (contact: any) => ({
          FirstName: contact.givenName || '',
          LastName: contact.familyName || '',
          Line1: contact.addressLines?.[0] || '',
          Line2: contact.addressLines?.[1] || '',
          Line3: contact.addressLines?.[2] || '',
          City: contact.locality || '',
          CountyName: contact.administrativeArea || '',
          PostalCode: contact.postalCode || '',
          CountryCode: contact.countryCode || 'IE',
          Phone: payment.shippingContact?.phoneNumber || '', // This is deliberate - phone numbber is sent by apple in shippingContact, but we need it in billing address
        });

        const billingAddress = convertApplePayAddress(payment.billingContact);
        const shippingAddress = convertApplePayAddress(payment.shippingContact);

        console.log('ApplePay: Billing address:', JSON.stringify(billingAddress));
        console.log('ApplePay: Shipping address:', JSON.stringify(shippingAddress));
        console.log('payment:', JSON.stringify(payment));

        // Use the final total from backend (already includes shipping, tax, everything)
        const finalTotal = currentAmountRef.current;
        console.log(
          'Current shipping options:',
          currentShippingOptionsRef.current,
        );
        console.log(
          'Selected shipping option:',
          selectedShippingOptionRef.current,
        );

        // Build the Apple Pay order
        const applePayOrder: ApplePayOrder = {
          // Billing address from Apple Pay
          BillingAddress: billingAddress,

          // Shipping address from Apple Pay
          ShippingAddress: shippingAddress,

          // Email from Apple Pay or checkout context
          Email: event.payment.shippingContact?.emailAddress || extendedCheckoutData?.email || '',

          // Guest status from checkout context
          IsGuest: extendedCheckoutData?.isGuest ?? true,

          // Only ship is available for Apple Pay
          DeliveryOption: 'Ship',

          // Apple Pay specific fields
          PaymentToken: payment.token.paymentData.data,
          SessionValidationToken: sessionValidationTokenRef.current,
          PaymentDataPayload: Buffer.from(JSON.stringify(event.payment)).toString('base64'),
          ScaData: buildScaData(),
          CardNetwork: payment.token.paymentMethod.network,
          CardLastFour: '', // Apple Pay doesn't provide this
          CardType: payment.token.paymentMethod.type,
          MerchantSessionIdentifier: (session as any).merchantSessionIdentifier,

          // Shipping option
          ShippingOptionId: selectedShippingOptionRef.current || undefined,
          ShippingCost: 0, // Shipping cost is included in TotalAmount from backend

          // CRITICAL: Include the total amount that matches Apple Pay's encrypted amount
          TotalAmount: finalTotal.toFixed(2),

          // Include tender data lines if available
          TenderDataLines: extendedCheckoutData?.tenderDataLines || [],

          // Buy Now properties
          IsBuyNow: isBuyNow,
          CartName: cartName || undefined,
        };

        console.log('ApplePay Debug: Final order amount from backend:', {
          backendTotal: currentAmountRef.current,
          finalTotal: finalTotal.toFixed(2),
          selectedShippingOption: selectedShippingOptionRef.current,
          NOTE: 'Using backend-calculated total (includes shipping, tax, etc.)',
        });

        console.log(
          'ApplePay Debug: Full Apple Pay event payment data:',
          JSON.stringify(event.payment, null, 2),
        );

        console.log('ApplePay: Creating order with complete address data:', {
          billingAddress: applePayOrder.BillingAddress,
          shippingAddress: applePayOrder.ShippingAddress,
          email: applePayOrder.Email,
        });
        console.log('ApplePay: Creating order with data:', applePayOrder);

        try {
          let orderResponse: ApplePayOrderResponse;

          // Create order using the hook
          console.log('ApplePay: Creating order');
          orderResponse = await new Promise<ApplePayOrderResponse>(
            (resolve, reject) => {
              createApplePayOrder(applePayOrder, {
                onError: (error: Error) => reject(error),
                onSuccess: (response: ApplePayOrderResponse) =>
                  resolve(response),
              });
            },
          );

          // Handle order response
          console.log('ApplePay: Order response received', orderResponse);

          // Handle sales order error
          if (orderResponse.salesOrderError) {
            const errorMessage =
              orderResponse.salesOrderError.message ||
              'Payment processing failed. Please try again.';
            NotificationsQueue.add(
              {
                notification: (
                  <NotificationMessage $isError={true}>
                    {errorMessage}
                  </NotificationMessage>
                ),
              },
              { timeout: 5000 },
            );

            session.completePayment(window.ApplePaySession.STATUS_FAILURE);
            return;
          }

          // Handle successful order
          if (orderResponse.isSuccess && orderResponse.data?.redirectUrl) {
            console.log(
              'ApplePay: Order created successfully, redirecting to:',
              orderResponse.data.redirectUrl,
            );
            session.completePayment(window.ApplePaySession.STATUS_SUCCESS);
            window.location.href = orderResponse.data.redirectUrl;
            return;
          }

          // Handle successful order with order number (legacy response format)
          if (orderResponse.orderNumber) {
            const confirmationUrl = `/checkout/order-confirmation?confirmationId=${orderResponse.orderNumber}`;
            session.completePayment(window.ApplePaySession.STATUS_SUCCESS);
            window.location.href = confirmationUrl;
            return;
          }

          // Handle error cases
          if (orderResponse.data?.errorMessage) {
            NotificationsQueue.add(
              {
                notification: (
                  <NotificationMessage $isError={true}>
                    {orderResponse.data.errorMessage}
                  </NotificationMessage>
                ),
              },
              { timeout: 5000 },
            );
            session.completePayment(window.ApplePaySession.STATUS_FAILURE);
            return;
          }

          // Handle generic failure
          if (
            !orderResponse.isSuccess &&
            orderResponse.messages &&
            orderResponse.messages.length > 0
          ) {
            NotificationsQueue.add(
              {
                notification: (
                  <NotificationMessage $isError={true}>
                    {orderResponse.messages[0]}
                  </NotificationMessage>
                ),
              },
              { timeout: 5000 },
            );
            session.completePayment(window.ApplePaySession.STATUS_FAILURE);
            return;
          }

          // Complete with success if we get here
          session.completePayment(window.ApplePaySession.STATUS_SUCCESS);
        } catch (orderError: any) {
          console.error('ApplePay: Order creation error:', orderError);
          NotificationsQueue.add(
            {
              notification: (
                <NotificationMessage $isError={true}>
                  {orderError.message || 'Payment processing failed'}
                </NotificationMessage>
              ),
            },
            { timeout: 5000 },
          );
          session.completePayment(window.ApplePaySession.STATUS_FAILURE);
        }
      } catch (error: any) {
        console.error('ApplePay: Payment processing error:', error);

        // Clear loading state on error
        setIsProcessingPayment(false);
        if (processingTimeoutRef.current) {
          clearTimeout(processingTimeoutRef.current);
        }

        session.completePayment(window.ApplePaySession.STATUS_FAILURE);

        NotificationsQueue.add(
          {
            notification: (
              <NotificationMessage $isError={true}>
                {error.message || 'Payment processing failed'}
              </NotificationMessage>
            ),
          },
          { timeout: 5000 },
        );
      }
    };

    session.oncancel = () => {
      setIsProcessingPayment(false);
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
    };
    session.begin();
  }, [
    checkoutData,
    applePaySettings,
    extendedCheckoutData,
    createApplePayOrder,
    fetchDeliveryOptions,
    refreshData,
    isBuyNow,
    productCode,
    cartName,
  ]);

  if (isRefreshing) {
    console.log('ApplePay: Not ready to render button', {
      isRefreshing,
      checkoutDataTotal: checkoutData?.amounts?.total,
    });
    return (
      <Styled.UnavailableMessage>
        Updating payment amount...
      </Styled.UnavailableMessage>
    );
  }

  console.log('ApplePayButtonWrapper: Rendering Apple Pay button');

  return (
    <Styled.ApplePayContainer>
      {(isPending || isProcessingPayment) && (
        <Styled.LoadingOverlay>
          <Styled.LoadingContent>
            <LoadingSpinner />
            <Styled.LoadingText aria-live="assertive" role="alert">
              Processing your order...
            </Styled.LoadingText>
          </Styled.LoadingContent>
        </Styled.LoadingOverlay>
      )}

      <Styled.ApplePayButton
        onClick={handleApplePayClick}
        disabled={isPending || isProcessingPayment}
        aria-label="Pay with Apple Pay"
      />
    </Styled.ApplePayContainer>
  );
};

export default ApplePayButtonWrapper;

